<template>
    <ValidationProvider v-slot="{ errors }" name="YoA" rules="required" :value="value">
      <AdvancedDropdown
        v-model="dropdownValue"
        v-bind="$attrs"
        :options="options"
        :disabled="disabled"
        :can-add-new="false"
        :label="label"
        size="sm"
      >
        <template v-slot:label v-if="label !== ''">
          Year of Account <span class="text-danger">*</span>
        </template>

        <!-- Short placeholder: this sits in a six-across row on the manual BDX
             step, so "Select year of account" clips mid-word. -->
        <template v-slot:button-content>
          <template v-if="!!selectedOption">{{ selectedOption.text }}</template>
          <template v-else>Select</template>
        </template>
      </AdvancedDropdown>

      <div v-if="!!errors.length" class="invalid-feedback d-block">
        {{ errors[0] }}
      </div>
    </ValidationProvider>
  </template>

  <script>
  import { defineComponent, computed } from '@vue/composition-api'
  import { ValidationProvider } from 'vee-validate'

  import { AdvancedDropdown } from '@components/advanced'

  export default defineComponent({
    name: 'CashCallFormYoA',
    components: {
      ValidationProvider,
      AdvancedDropdown
    },
    props: {
      value: Number,
      required: Boolean,
      // Read-only by default: on the normal path the year of account is derived
      // from the selected BDX key. LIQ-1006's manual path has no key to derive
      // it from, so it passes :disabled="false" to let the user choose one.
      disabled: {
        type: Boolean,
        default: true
      },
      // Pass label="" to render the dropdown with no label of its own.
      label: {
        type: String,
        default: 'Year of Account'
      }
    },
    emits: ['input'],
    setup(props, { emit }) {
      // AdvancedDropdown is array-valued (it reads value[0] and emits an
      // array), but this field is stored as a plain Number. Bridge between the
      // two here so callers keep binding a scalar - switching to
      // AdvancedDropdown is a presentation change only. It also keeps the
      // option list inside a scrollable menu in the page, rather than the
      // native <select> popup the browser draws over the whole modal.
      const dropdownValue = computed({
        get: () => (props.value ? [props.value] : []),
        set: (newValue) => {
          const picked = Array.isArray(newValue) ? newValue[0] : newValue
          emit('input', picked ? Number(picked) : 0)
        }
      })

      const options = computed(() => {
        const startYear = 2006
        const endYear = new Date().getFullYear()

        return Array.from(
          { length: endYear - startYear + 1 },
          (_, i) => startYear + i
        )
          .reverse()
          .map((year) => ({ text: String(year), value: year }))
      })

      const selectedOption = computed(() => {
        if (!props.value) return null
        return options.value.find((opt) => opt.value === Number(props.value)) || null
      })

      return {
        dropdownValue,
        options,
        selectedOption
      }
    }
  })
  </script>
  <style scoped>
    /* Fill the column rather than a fixed 214px: on the manual BDX step this
       sits in a narrow six-across row, where a fixed width overflows its
       column and clips the text. Ellipsis covers any remaining overflow. */
    .advanced-dropdown >>> .dropdown-toggle {
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding-right: 1.75rem;
    }
    /* The shared menu is min-width:300px, which spills out of these narrow
       columns - let it size to the field instead. */
    .advanced-dropdown >>> .dropdown-menu {
      min-width: 100%;
      /* Shorter than the shared 320px: these two sit low in a six-across row on
         the manual BDX step, so a tall menu runs past the bottom of the modal.
         ~5 options plus the search bar is enough to scroll comfortably. */
      max-height: 210px;
    }
  </style>
